import { useEffect, useState } from "react";
import { T } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn } from "../components";

// ── Small helpers ─────────────────────────────────────────────────
const StatusDot = ({ on }) => (
  <span style={{
    width: 8, height: 8, borderRadius: "50%", flexShrink: 0, display: "inline-block",
    background: on ? "#22c55e" : T.text3,
    boxShadow: on ? "0 0 0 3px rgba(34,197,94,0.2)" : "none",
    animation: on ? "pulse 2s infinite" : "none",
  }} />
);

const FieldBadge = ({ label, value, color }) => (
  <div style={{ padding: "10px 14px", borderRadius: 10, background: T.bg2, border: `1px solid ${T.border1}` }}>
    <div style={{ fontSize: 10, color: T.text3, marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, color: color || T.text1 }}>{value || "—"}</div>
  </div>
);

const InputField = ({ label, value, onChange, placeholder, type = "text", hint }) => {
  const [show, setShow] = useState(false);
  const isPass = type === "password";
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={isPass && !show ? "password" : "text"}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ width: "100%", padding: isPass ? "10px 38px 10px 12px" : "10px 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text1, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
          onFocus={e => (e.target.style.borderColor = T.indigo)}
          onBlur={e  => (e.target.style.borderColor = T.border2)}
        />
        {isPass && (
          <button onClick={() => setShow(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.text3, display: "flex", padding: 2 }}>
            {show
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            }
          </button>
        )}
      </div>
      {hint && <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────
export default function ShopifyPage({ token, plan, setPage }) {
  const [status,      setStatus]      = useState(null);
  const [products,    setProducts]    = useState([]);
  const [domain,      setDomain]      = useState("");
  const [clientId,    setClientId]    = useState("");
  const [clientSecret,setClientSecret]= useState("");
  const [loading,        setLoading]        = useState(true);
  const [connecting,     setConnecting]     = useState(false);
  const [error,          setError]          = useState("");
  const [success,        setSuccess]        = useState("");
  const [prodLoad,       setProdLoad]       = useState(false);
  const [showGuide,      setShowGuide]      = useState(false);
  const [webhookReg,     setWebhookReg]     = useState(false);
  const [regLoading,     setRegLoading]     = useState(false);

  const isPro = plan && plan !== "free";

  // On mount: load status + handle OAuth callback
  useEffect(() => {
    if (!isPro) { setLoading(false); return; }

    const params = new URLSearchParams(window.location.search);
    if (params.get("shopify_oauth") === "success") {
      const shopName = params.get("shop") || "your store";
      setSuccess(`Connected to ${shopName} successfully!`);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("shopify_oauth") === "error") {
      const msg = params.get("msg") || "OAuth connection failed — please try again.";
      setError(decodeURIComponent(msg));
      window.history.replaceState({}, "", window.location.pathname);
    }

    apiFetch("/api/shopify/status", {}, token)
      .then(d => {
        setStatus(d);
        setWebhookReg(d.webhookRegistered || false);
        if (d.clientId) setClientId(d.clientId);
        if (d.domain)   setDomain(d.domain);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token, isPro]);

  // Load products when connected
  useEffect(() => {
    if (!status?.connected) return;
    setProdLoad(true);
    apiFetch("/api/shopify/products", {}, token)
      .then(d => setProducts(d.products || []))
      .catch(() => {})
      .finally(() => setProdLoad(false));
  }, [status?.connected, token]);

  const connectViaOAuth = async (e) => {
    e?.preventDefault(); e?.stopPropagation();
    setError(""); setSuccess("");
    if (!domain.trim())       return setError("Store domain is required");
    if (!clientId.trim())     return setError("Client ID is required");
    if (!clientSecret.trim()) return setError("Client Secret is required");
    setConnecting(true);
    try {
      const d = await apiFetch(
        `/api/shopify/oauth/start?shop=${encodeURIComponent(domain.trim())}&clientId=${encodeURIComponent(clientId.trim())}&clientSecret=${encodeURIComponent(clientSecret.trim())}`,
        {}, token
      );
      if (d.authUrl) {
        window.location.href = d.authUrl;
      } else {
        setError(d.error || "Failed to start OAuth");
        setConnecting(false);
      }
    } catch (err) {
      setError(err?.message || "Network error");
      setConnecting(false);
    }
  };

  const sendTestOrder = async () => {
    setError(""); setSuccess("");
    const phone = prompt("Enter your WhatsApp number (with country code, no +):\ne.g. 923001234567");
    if (!phone?.trim()) return;
    setConnecting(true);
    try {
      const d = await apiFetch("/api/shopify/test-order", {
        method: "POST",
        body: JSON.stringify({ phone: phone.trim() }),
      }, token);
      if (d.success) setSuccess(`Test order sent to +${phone}! Check E-com Pipeline — a new order card should appear and a WhatsApp message will be sent.`);
      else setError(d.error || "Test failed");
    } catch (e) { setError(e.message || "Failed"); }
    setConnecting(false);
  };

  const registerWebhook = async () => {
    setRegLoading(true); setError(""); setSuccess("");
    try {
      const d = await apiFetch("/api/shopify/register-webhook", { method: "POST" }, token);
      if (d.success) { setWebhookReg(true); setSuccess("Order webhook registered! New Shopify orders will now appear in E-com Pipeline automatically."); }
      else setError(d.error || "Webhook registration failed");
    } catch (e) { setError(e.message || "Failed"); }
    setRegLoading(false);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Shopify? Your store won't be accessible to the bot.")) return;
    await apiFetch("/api/shopify/disconnect", { method: "DELETE" }, token);
    setStatus({ connected: false, enabled: false, domain: "", shopName: "", currency: "" });
    setProducts([]);
    setClientId(""); setClientSecret(""); setDomain("");
    setSuccess("");
  };

  const toggleEnabled = async (val) => {
    await apiFetch("/api/shopify/toggle", { method: "PATCH", body: JSON.stringify({ enabled: val }) }, token);
    setStatus(s => ({ ...s, enabled: val }));
  };

  // ── Pro gate ──────────────────────────────────────────────────────
  if (!isPro) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ maxWidth: 440, textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: 20, margin: "0 auto 24px",
          background: "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.10))",
          border: "1px solid rgba(99,102,241,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="1.8">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.text1, marginBottom: 10, letterSpacing: "-0.02em" }}>Shopify is a Pro Feature</div>
        <div style={{ fontSize: 14, color: T.text3, lineHeight: 1.6, marginBottom: 28 }}>
          Connect your Shopify store and let your WhatsApp bot automatically answer product and order questions.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {["Answer product questions automatically", "Look up customer order status by phone", "Show live prices & stock availability", "Handle returns & shipping queries with AI"].map(f => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: T.bg2, border: `1px solid ${T.border1}`, textAlign: "left" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span style={{ fontSize: 13, color: T.text2 }}>{f}</span>
            </div>
          ))}
        </div>
        <button onClick={() => setPage?.("plan")} style={{
          width: "100%", padding: "13px 24px", borderRadius: 12, border: "none", cursor: "pointer",
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff",
          fontSize: 14, fontWeight: 700, boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
        }}>
          Upgrade Plan — From $29/mo
        </button>
        <div style={{ fontSize: 12, color: T.text3, marginTop: 12 }}>No contracts · Cancel anytime</div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 28, height: 28, border: `3px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "26px 28px 50px" }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: "linear-gradient(135deg, #96bf48, #5e8e3e)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 14px rgba(150,191,72,0.35)",
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text1, letterSpacing: "-0.02em" }}>Shopify Integration</div>
            <div style={{ fontSize: 13, color: T.text3, marginTop: 2 }}>Connect your store using your Shopify app credentials</div>
          </div>
          {status?.connected && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 12, color: T.text3 }}>{status.enabled ? "Bot active" : "Bot paused"}</div>
              <div onClick={() => toggleEnabled(!status.enabled)} style={{
                width: 40, height: 22, borderRadius: 11, cursor: "pointer", transition: "background 0.2s", position: "relative",
                background: status.enabled ? T.indigo : T.border2,
              }}>
                <div style={{
                  position: "absolute", top: 3, left: status.enabled ? 21 : 3,
                  width: 16, height: 16, borderRadius: "50%", background: "#fff",
                  transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status bar */}
      {status?.connected ? (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderRadius: 14, background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)", marginBottom: 16 }}>
            <StatusDot on={true} />
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Connected to Shopify</div>
            <div style={{ fontSize: 12, color: T.text3, marginLeft: 4 }}>— {status.shopName}</div>
            <button onClick={disconnect} style={{
              marginLeft: "auto", padding: "5px 14px", borderRadius: 8, border: `1px solid ${T.rose}44`,
              background: "transparent", color: T.rose, fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>Disconnect</button>
          </div>
          <div className="grid-4" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 12 }}>
            <FieldBadge label="Store"      value={status.shopName} color={T.text1} />
            <FieldBadge label="Domain"     value={status.domain} />
            <FieldBadge label="Currency"   value={status.currency} color={T.indigo} />
            <FieldBadge label="Bot Status" value={status.enabled ? "Active" : "Paused"} color={status.enabled ? "#22c55e" : T.text3} />
            <FieldBadge label="Order Pipeline" value={webhookReg ? "Active" : "Not set up"} color={webhookReg ? "#22c55e" : "#f59e0b"} />
          </div>
          {/* Webhook registration prompt */}
          {!webhookReg && (
            <div style={{ marginTop: 12, padding: "12px 16px", borderRadius: 12, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.25)", display: "flex", alignItems: "center", gap: 12 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>E-com Pipeline not active</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Register the order webhook so new Shopify orders automatically appear in the E-com Pipeline</div>
              </div>
              <button onClick={registerWebhook} disabled={regLoading} style={{
                padding: "7px 16px", borderRadius: 9, border: "none", cursor: regLoading ? "not-allowed" : "pointer",
                background: "#f59e0b", color: "#fff", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", opacity: regLoading ? 0.7 : 1,
              }}>
                {regLoading ? "Registering…" : "Register Webhook"}
              </button>
            </div>
          )}
          {webhookReg && (
            <div style={{ marginTop: 12, padding: "10px 16px", borderRadius: 12, background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)", display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <div style={{ flex: 1, fontSize: 12, color: T.text2 }}>Order webhook active — new Shopify orders appear in <strong style={{ color: T.text1 }}>E-com Pipeline</strong> automatically</div>
              <button onClick={sendTestOrder} style={{
                padding: "5px 14px", borderRadius: 8, border: `1px solid ${T.border2}`,
                background: T.bg2, color: T.text1, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
              }}>
                Send Test Order
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 12, background: T.bg2, border: `1px solid ${T.border1}`, marginBottom: 24 }}>
          <StatusDot on={false} />
          <div style={{ fontSize: 13, color: T.text3 }}>Not connected — fill in your Shopify app credentials below</div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: status?.connected ? "1fr 1fr" : "1fr", gap: 20 }}>

        {/* Connect form */}
        <div style={{ padding: "22px 24px", borderRadius: 16, background: T.bg1, border: `1px solid ${T.border1}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 4 }}>
            {status?.connected ? "Re-connect / Update Credentials" : "Connect Your Shopify Store"}
          </div>
          <div style={{ fontSize: 12, color: T.text3, marginBottom: 6, lineHeight: 1.6 }}>
            Enter your Shopify app credentials. Each store uses its own Client ID and Secret from the Shopify Partner Dashboard.
          </div>

          {/* How to get credentials toggle */}
          <button onClick={() => setShowGuide(v => !v)} style={{
            fontSize: 12, color: T.indigo, background: "none", border: "none", cursor: "pointer", padding: "0 0 16px", display: "flex", alignItems: "center", gap: 4,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {showGuide ? "Hide" : "How to get your credentials?"}
          </button>

          {showGuide && (
            <div style={{ padding: "14px 16px", borderRadius: 10, background: T.bg2, border: `1px solid ${T.border1}`, marginBottom: 16, fontSize: 12, color: T.text2, lineHeight: 1.8 }}>
              <div style={{ fontWeight: 700, color: T.text1, marginBottom: 8 }}>Steps to get Client ID + Secret:</div>
              <div>1. Go to <span style={{ color: T.indigo }}>partners.shopify.com</span></div>
              <div>2. Click <b>Apps</b> → <b>Create app</b> → <b>Create app manually</b></div>
              <div>3. Under <b>App setup</b> → URLs, add this redirect URL:</div>
              <div style={{ margin: "6px 0", padding: "6px 10px", background: T.bg3 || T.bg1, borderRadius: 6, fontFamily: "monospace", fontSize: 11, wordBreak: "break-all", color: T.indigo }}>
                https://whatappbot-production.up.railway.app/api/shopify/oauth/callback
              </div>
              <div>4. Save — then copy <b>Client ID</b> and <b>Client Secret</b> from the Credentials section</div>
              <div style={{ marginTop: 8, color: T.text3 }}>You only need to do this once per store.</div>
            </div>
          )}

          <InputField
            label="Store Domain"
            value={domain}
            onChange={setDomain}
            placeholder="yourstore.myshopify.com"
            hint="Just the domain — no https://"
          />
          <InputField
            label="Client ID"
            value={clientId}
            onChange={setClientId}
            placeholder="e.g. 88f4f8bb6d1e3165045b798a3850c740"
            hint="From Shopify Partner Dashboard → App → Credentials"
          />
          <InputField
            label="Client Secret"
            value={clientSecret}
            onChange={setClientSecret}
            placeholder="shpss_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            type="password"
            hint="From Shopify Partner Dashboard → App → Credentials"
          />

          {error   && <div style={{ padding: "10px 14px", borderRadius: 9, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: T.rose, fontSize: 13, marginBottom: 14 }}>{error}</div>}
          {success && <div style={{ padding: "10px 14px", borderRadius: 9, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#22c55e", fontSize: 13, marginBottom: 14 }}>{success}</div>}

          <button
            onClick={connectViaOAuth}
            disabled={connecting}
            style={{
              width: "100%", padding: "12px", borderRadius: 10, border: "none",
              cursor: connecting ? "not-allowed" : "pointer",
              background: "linear-gradient(135deg,#96bf48,#5e8e3e)", color: "#fff",
              fontSize: 14, fontWeight: 700, opacity: connecting ? 0.7 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: "0 4px 16px rgba(94,142,62,0.35)",
            }}
          >
            {connecting
              ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Redirecting to Shopify…</>
              : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg> Connect via Shopify</>
            }
          </button>
        </div>

        {/* Products preview */}
        {status?.connected && (
          <div style={{ padding: "22px 24px", borderRadius: 16, background: T.bg1, border: `1px solid ${T.border1}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 4 }}>Product Catalog Preview</div>
            <div style={{ fontSize: 12, color: T.text3, marginBottom: 16 }}>First 10 active products the bot can answer questions about</div>

            {prodLoad ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <div style={{ width: 24, height: 24, border: `2px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              </div>
            ) : products.length === 0 ? (
              <div style={{ textAlign: "center", color: T.text3, fontSize: 13, padding: "24px 0" }}>No active products found</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {products.map(p => {
                  const prices  = [...new Set((p.variants || []).map(v => v.price))];
                  const inStock = (p.variants || []).some(v => v.inventory_quantity > 0 || v.inventory_management === null);
                  const img     = p.image?.src;
                  return (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, background: T.bg2, border: `1px solid ${T.border1}` }}>
                      {img ? (
                        <img src={img} alt={p.title} style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: T.bg2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                        <div style={{ fontSize: 11, color: T.text3 }}>{prices.length ? `${status.currency} ${prices.join(" / ")}` : "No price"}</div>
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 20,
                        background: inStock ? "rgba(34,197,94,0.1)" : "rgba(244,63,94,0.1)",
                        color: inStock ? "#22c55e" : T.rose,
                        border: `1px solid ${inStock ? "rgba(34,197,94,0.25)" : "rgba(244,63,94,0.25)"}`,
                      }}>{inStock ? "In Stock" : "Out"}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* How it works */}
      <div style={{ marginTop: 28, padding: "22px 24px", borderRadius: 16, background: T.bg1, border: `1px solid ${T.border1}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 16 }}>How the bot uses your Shopify data</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 12 }}>
          {[
            { icon: "📦", title: "Order Status",   desc: "Customer asks about their order → bot looks it up by their WhatsApp number automatically" },
            { icon: "🛍️", title: "Product Info",   desc: "Questions about price, availability, descriptions → answered from live Shopify catalog" },
            { icon: "📊", title: "Stock Check",    desc: "Is this in stock? Bot checks real-time inventory from your store" },
            { icon: "🤝", title: "Human Handoff",  desc: "Complex issues like refunds → bot escalates to your team as usual" },
          ].map(({ icon, title, desc }) => (
            <div key={title} style={{ padding: "14px 16px", borderRadius: 12, background: T.bg2, border: `1px solid ${T.border1}` }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text1, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.6 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
